/**
 * Kompakte Transportleiste: Play, Stop, Loop und Statusanzeige.
 *
 * Die Ansicht spiegelt den Zustand des {@link AudioTransport} und leitet
 * Benutzeraktionen ausschließlich an ihn weiter. Sie enthält selbst keine
 * Audio- oder Wiedergabelogik.
 */

import { INSTRUMENT_LABELS, type DrumInstrument } from '../model/types';
import type {
  AudioTransport,
  PlaybackState,
  TransportSnapshot,
} from '../audio/AudioTransport';

const PLAY_LABELS: Readonly<Record<PlaybackState, string>> = {
  idle: 'Abspielen',
  loading: 'Samples laden …',
  playing: 'Wiedergabe läuft',
  stopped: 'Abspielen',
  error: 'Erneut versuchen',
};

function statusText(snapshot: TransportSnapshot): string {
  const failed = snapshot.failedInstruments;
  const failedNote =
    failed.length > 0
      ? ` – ohne ${failed
          .map((instrument: DrumInstrument) => INSTRUMENT_LABELS[instrument])
          .join(', ')} (Sample fehlt)`
      : '';
  switch (snapshot.state) {
    case 'idle':
      return 'Bereit';
    case 'loading':
      return 'Samples werden geladen …';
    case 'playing':
      return `Wiedergabe läuft${snapshot.loop ? ' (Loop)' : ''}${failedNote}`;
    case 'stopped':
      return `Gestoppt${failedNote}`;
    case 'error':
      return 'Audio konnte nicht gestartet werden.';
  }
}

export class TransportBar {
  readonly #transport: AudioTransport;
  #unsubscribe: (() => void) | null = null;

  #playButton: HTMLButtonElement | null = null;
  #stopButton: HTMLButtonElement | null = null;
  #loopInput: HTMLInputElement | null = null;
  #status: HTMLElement | null = null;

  constructor(transport: AudioTransport) {
    this.#transport = transport;
  }

  mount(container: HTMLElement): void {
    const bar = document.createElement('div');
    bar.className = 'transport';
    bar.setAttribute('role', 'group');
    bar.setAttribute('aria-label', 'Wiedergabe');

    const play = document.createElement('button');
    play.type = 'button';
    play.className = 'button button--primary transport__play';
    play.textContent = PLAY_LABELS.idle;
    play.addEventListener('click', () => {
      void this.#transport.play();
    });
    this.#playButton = play;

    const stop = document.createElement('button');
    stop.type = 'button';
    stop.className = 'button transport__stop';
    stop.textContent = 'Stop';
    stop.addEventListener('click', () => {
      this.#transport.stop();
    });
    this.#stopButton = stop;

    const loopLabel = document.createElement('label');
    loopLabel.className = 'transport__loop';
    const loop = document.createElement('input');
    loop.type = 'checkbox';
    loop.addEventListener('change', () => {
      this.#transport.setLoop(loop.checked);
    });
    const loopText = document.createElement('span');
    loopText.textContent = 'Loop';
    loopLabel.append(loop, loopText);
    this.#loopInput = loop;

    const status = document.createElement('span');
    status.className = 'transport__status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    this.#status = status;

    bar.append(play, stop, loopLabel, status);
    container.appendChild(bar);

    this.#unsubscribe = this.#transport.subscribe((snapshot) => {
      this.#render(snapshot);
    });
  }

  destroy(): void {
    this.#unsubscribe?.();
    this.#unsubscribe = null;
  }

  #render(snapshot: TransportSnapshot): void {
    if (this.#playButton) {
      this.#playButton.textContent = PLAY_LABELS[snapshot.state];
      const busy = snapshot.state === 'loading' || snapshot.state === 'playing';
      this.#playButton.disabled = busy;
      this.#playButton.setAttribute('aria-busy', String(busy));
    }
    if (this.#stopButton) {
      const active =
        snapshot.state === 'playing' || snapshot.state === 'loading';
      this.#stopButton.disabled = !active;
    }
    if (this.#loopInput) {
      this.#loopInput.checked = snapshot.loop;
    }
    if (this.#status) {
      this.#status.textContent = statusText(snapshot);
      this.#status.classList.toggle(
        'transport__status--error',
        snapshot.state === 'error',
      );
    }
  }
}
